import { FaCircleInfo } from 'react-icons/fa6';
import {
  Button,
  clsxMerge,
  Tooltip,
  TooltipTriggerWrapper,
  Typography,
} from '@breezeai-frontend/cargo-ui';

import { formatPrice } from '../../../../../../packages/cargo-ui/src/utils/tools';
import {
  useFeatureToggle,
  useUserSetting,
} from '../../../context/auth/auth-hooks';
import { type SupportedCurrencies } from '../../../model/CurrencyValue';
import { getAppLocale } from '../../../utils/locale';
import { useQuoteCosts } from '../../hooks/useQuoteCosts';
import { type QuotePriceProperties } from '../../types/quote';

interface QuotePriceBreakdownTooltipProps extends QuotePriceProperties {
  premium_value: number;
  premium_currency: SupportedCurrencies;
  showExchangeRate?: boolean;
  className?: string;
  defaultOpen?: React.ComponentProps<
    typeof TooltipTriggerWrapper
  >['defaultOpen'];
}

export function QuotePriceBreakdownTooltip(
  props: QuotePriceBreakdownTooltipProps,
) {
  const {
    exchange_rate,
    converted_customer_premium_value = 0,
    converted_tax_amount = 0,
    hide_tax_info,
    showExchangeRate = true,
    className,
    defaultOpen = false,
  } = props;

  const { premiumCost, premiumCurrency, taxCost, taxCurrency, totalCost } =
    useQuoteCosts(props);

  const locale = getAppLocale();
  const showExchangeRateFeatureToggle = useFeatureToggle(
    'show_currencies_exchange_rates',
  );
  const showPremiumsInDefaultCurrency = useUserSetting<boolean>(
    'show_premiums_in_default_currency',
  );

  const premiumPrice = formatPrice({
    currency: premiumCurrency,
    value: premiumCost,
    locale,
  });

  const taxPrice =
    taxCost && taxCurrency
      ? formatPrice({
          currency: taxCurrency,
          value: taxCost,
          locale,
        })
      : '';

  const totalPrice = formatPrice({
    currency: premiumCurrency,
    value: totalCost,
    locale,
  });

  const convertedTotal =
    converted_customer_premium_value + converted_tax_amount;
  const convertedTotalPrice = exchange_rate
    ? formatPrice({
        currency: exchange_rate.to_currency.code,
        value: convertedTotal,
        locale,
      })
    : 0;

  const commonTextClassNames = 'text-sm leading-4';
  const iconDefaultClassName = 'w-[18px] h-[18px] text-system-grey-750';

  const shouldShowCurrencyConversion =
    showExchangeRate &&
    showExchangeRateFeatureToggle &&
    exchange_rate &&
    !showPremiumsInDefaultCurrency;

  return (
    <TooltipTriggerWrapper delay={0} defaultOpen={defaultOpen}>
      <Button
        data-testid="quote-price-breakdown-tooltip-trigger"
        variant="ghost"
        customStyles="p-0 w-min h-min min-w-0 rounded-full hover:bg-[none] text-system-grey-750"
      >
        <FaCircleInfo className={clsxMerge(iconDefaultClassName, className)} />
      </Button>
      <Tooltip
        data-testid="quote-price-breakdown-tooltip"
        placement="top"
        offset={16}
        showArrow={false}
        className={clsxMerge(
          'flex gap-2 flex-col py-3 px-0',
          !hide_tax_info && 'min-w-[205px]',
        )}
      >
        {!hide_tax_info && (
          <>
            <div className="flex items-center gap-2 mx-3">
              <FaCircleInfo className={iconDefaultClassName} />
              {/* eslint-disable-next-line i18next/no-literal-string -- TODO fix localization */}
              <Typography customStyles="font-bold">Price Details</Typography>
            </div>
            <div className="flex gap-2 justify-between mx-3">
              <Typography
                customStyles={clsxMerge(
                  commonTextClassNames,
                  'text-system-grey-700',
                )}
                // eslint-disable-next-line i18next/no-literal-string -- TODO fix localization
              >
                Premium
              </Typography>
              <Typography customStyles={commonTextClassNames}>
                {premiumPrice}
              </Typography>
            </div>
            {!!taxPrice && (
              <div className="flex gap-2 justify-between mx-3">
                <Typography
                  customStyles={clsxMerge(
                    commonTextClassNames,
                    'text-system-grey-700',
                  )}
                  // eslint-disable-next-line i18next/no-literal-string -- TODO fix localization
                >
                  Taxes
                </Typography>
                <Typography customStyles={commonTextClassNames}>
                  {taxPrice}
                </Typography>
              </div>
            )}
            <div className="flex gap-2 justify-between mx-1 bg-system-grey-70 p-2 rounded-lg">
              <Typography
                customStyles={clsxMerge(commonTextClassNames, 'font-bold')}
                // eslint-disable-next-line i18next/no-literal-string -- TODO fix localization
              >
                Total
              </Typography>
              <Typography
                customStyles={clsxMerge(commonTextClassNames, 'font-bold')}
              >
                {totalPrice}
              </Typography>
            </div>
          </>
        )}

        {!!taxPrice && hide_tax_info && shouldShowCurrencyConversion && (
          // eslint-disable-next-line i18next/no-literal-string -- TODO fix localization
          <span className="mx-3">Price includes all fees and taxes</span>
        )}

        {!!taxPrice && hide_tax_info && !shouldShowCurrencyConversion && (
          // eslint-disable-next-line i18next/no-literal-string -- TODO fix localization
          <span className="mx-3">Includes all fees and taxes</span>
        )}

        {shouldShowCurrencyConversion && (
          <>
            <div className="flex gap-2 justify-between mx-3">
              {/* eslint-disable-next-line i18next/no-literal-string -- TODO fix localization */}
              <Typography customStyles={commonTextClassNames}>
                Total in {exchange_rate.to_currency.code}
              </Typography>
              <Typography customStyles={commonTextClassNames}>
                {convertedTotalPrice}
              </Typography>
            </div>
            <Typography
              customStyles={clsxMerge(
                commonTextClassNames,
                'text-system-grey-700 mx-3',
              )}
              // eslint-disable-next-line i18next/no-literal-string -- TODO fix localization
            >
              Exchange rate: {exchange_rate.exchange_rate.toFixed(4)}{' '}
              {exchange_rate.to_currency.code}
            </Typography>
          </>
        )}
      </Tooltip>
    </TooltipTriggerWrapper>
  );
}
